import { Box } from '@chakra-ui/react';
import {
  InfoCard,
  Text,
} from '@ayble-health/dashboard-components';
import { useParams } from 'react-router-dom';

import { useMeetingNotes } from '../../api/healthCoach/useMeetingNotes';
import { MeetingStatusBadge } from './MeetingStatusBadge';
import {
  computeMeetingToDisplay,
  formatDateInBrowserTZ,
  getMeetingGoalsByMeetingType,
} from './utils';
import { useUserFeedbackSurvey } from '../../api/healthCoach/useUserFeedback';
import MeetingCardEditor from './MeetingCardEditor/MeetingCardEditor';
import { Loader } from '../Loader';
import { useCreateMeetingNote } from '../../api/healthCoach/mutations/useCreateMeetingNote';
import { ChartNote } from '../../api/types';
import UpcomingMeetingChartNotes from './UpcomingMeetingChartNotes';

type Props = {
  onOpenChartNote: (chartingNoteUrl: string | null) => void;
};

export const UpcomingMeeting = ({ onOpenChartNote }: Props) => {
  const { patient } = useParams();
  const { meetingNotes, isLoading, isError } = useMeetingNotes(patient ?? '');
  const { userFeedbackSurveyData } = useUserFeedbackSurvey(patient ?? '');
  const meeting = computeMeetingToDisplay(meetingNotes ?? []);
  const { mutation: createMeetingNoteMutation } = useCreateMeetingNote(
    ({ note_url }) => onOpenChartNote(note_url)
  );

  if (isLoading) {
    return (
      <Box p={0}>
        <Loader loading={true} />
      </Box>
    );
  }
  if (isError || !meeting) return null;

  const {
    client_confirmed,
    meeting_date,
    meeting_type,
    provider,
    pm_status,
    zoom_start_url,
    healthie_appointment_id,
  } = meeting;

  const { display_name, image_url } = provider || {};

  const pillText = formatDateInBrowserTZ(meeting_date);

  const meetingGoals = getMeetingGoalsByMeetingType(
    userFeedbackSurveyData ?? [],
    meeting_type ?? ''
  );

  const handleOpenChartNote = ({ note_url, note_type }: ChartNote) => {
    if (note_url) {
      onOpenChartNote(note_url);
    } else {
      createMeetingNoteMutation.mutate({
        meeting_id: healthie_appointment_id,
        note_type,
      });
    }
  };

  return (
    <InfoCard
      avatarUrl={image_url}
      borderTopRadius={'md'}
      borderBottomRadius={'xl'}
      content={
        <Text color={'blueGray.600'} fontSize={'md'} fontWeight={'normal'}>
          <>
            <MeetingStatusBadge
              isConfirmed={client_confirmed}
              pmStatus={pm_status}
            />
            {meetingGoals ? `"${meetingGoals}"` : 'No visit notes.'}
          </>
        </Text>
      }
      headingPill={pillText}
      my={6}
      onPrimaryButtonClick={() => {
        if (!zoom_start_url) return;

        window.open(zoom_start_url, '_blank')?.focus();
      }}
      primaryButtonText={'Join Visit'}
      subtitle={display_name}
      stretchButtons
      title={meeting_type ?? ''}
      titleSize={'lg'}
    >
      <UpcomingMeetingChartNotes
        chartNotes={meeting.chart_notes}
        onClick={handleOpenChartNote}
      />
      <MeetingCardEditor meeting={meeting} />
    </InfoCard>
  );
};
